import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  getAuthToken,
  setAuthToken,
  clearAuthToken,
  getStoredUser,
  setStoredUser,
  clearStoredUser,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
  setTokenExpiryFromSeconds,
  clearTokenExpiry,
  isTokenExpired,
  clearAllAuth,
  dispatchAuthLogout,
  getActiveAccountId,
  setActiveAccountId,
  AUTH_LOGOUT_EVENT,
  type AuthLogoutDetail,
  type StoredUser,
} from '../auth';
import * as authApi from '../api/auth';
import { fetchMe, setActiveWorkspace } from '../api/accounts';
import type {
  AccountSummary,
  CurrentUser,
} from '../../../../shared/types/accounts';

export interface AuthState {
  user: StoredUser | null;
  token: string | null;
  isAuthenticated: boolean;

  isLoggingIn: boolean;
  loginError: string | null;
  /** Resolves on success; throws on failure so callers can branch. */
  login: (email: string, password: string) => Promise<void>;

  isSigningUp: boolean;
  signupError: string | null;
  /** Returns the SignupResult so the page can navigate to /confirm. */
  signup: (email: string, password: string) => Promise<authApi.SignupResult>;

  isConfirming: boolean;
  confirmError: string | null;
  confirm: (email: string, code: string) => Promise<void>;

  resendCode: (email: string) => Promise<void>;

  logout: () => Promise<void>;

  // Multi-tenant identity — populated by GET /auth/me on mount + after login.
  me: CurrentUser | null;
  /** The account the UI is currently scoped to. Derived from the
   *  persisted selection, falling back to the first account. */
  currentAccount: AccountSummary | null;
  /** Switch the active workspace. Persists across reloads. */
  setCurrentAccount: (accountId: string) => void;
  /** True before the first /auth/me round-trip resolves. */
  meLoading: boolean;
  /** Forces another /auth/me — call after onboarding or invite acceptance. */
  refreshMe: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

interface AuthProviderProps { children: React.ReactNode; }

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => getAuthToken());
  const [user, setUser] = useState<StoredUser | null>(() => getStoredUser());

  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const [isSigningUp, setIsSigningUp] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);

  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const [me, setMe] = useState<CurrentUser | null>(null);
  const [meLoading, setMeLoading] = useState<boolean>(false);
  // Persisted active-account selection. The derived `currentAccount`
  // below reconciles this against the account list from /auth/me.
  const [activeAccountId, setActiveAccountIdState] = useState<string | null>(
    () => getActiveAccountId(),
  );

  const setCurrentAccount = useCallback((accountId: string) => {
    setActiveAccountId(accountId);
    setActiveAccountIdState(accountId);
    // Persist server-side so tenant scoping doesn't depend on the client
    // sending ?account= — the backend defaults data scope to this workspace.
    // Fire-and-forget: the ?account= param still narrows this session.
    void setActiveWorkspace(accountId).catch((err) => {
      console.warn('[auth] setActiveWorkspace failed', { err: (err as Error).message });
    });
  }, []);

  const refreshMe = useCallback(async () => {
    if (!getAuthToken()) {
      setMe(null);
      return;
    }
    setMeLoading(true);
    try {
      const u = await fetchMe();
      setMe(u);
    } catch (err) {
      console.warn('[auth] /auth/me failed', { err: (err as Error).message });
    } finally {
      setMeLoading(false);
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    setIsLoggingIn(true);
    setLoginError(null);
    try {
      const result = await authApi.login(email, password);
      setAuthToken(result.token);
      if (result.refreshToken) setRefreshToken(result.refreshToken);
      setTokenExpiryFromSeconds(result.expiresIn);
      setStoredUser(result.user);
      setToken(result.token);
      setUser(result.user);
      // Pull the account list now so /onboard redirects fire on the
      // first protected render after login.
      void refreshMe();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed';
      setLoginError(msg);
      throw err;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  const signup = useCallback(async (email: string, password: string) => {
    setIsSigningUp(true);
    setSignupError(null);
    try {
      return await authApi.signup(email, password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Signup failed';
      setSignupError(msg);
      throw err;
    } finally {
      setIsSigningUp(false);
    }
  }, []);

  const confirm = useCallback(async (email: string, code: string) => {
    setIsConfirming(true);
    setConfirmError(null);
    try {
      await authApi.confirmSignup(email, code);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Confirmation failed';
      setConfirmError(msg);
      throw err;
    } finally {
      setIsConfirming(false);
    }
  }, []);

  const resendCode = useCallback(async (email: string) => {
    await authApi.resendCode(email);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    clearAllAuth();
    setToken(null);
    setUser(null);
    dispatchAuthLogout({ reason: 'manual' });
  }, []);

  // ---------------------------------------------------------------------
  // Global logout listener — authFetch fires AUTH_LOGOUT_EVENT when a
  // request fails because the session has expired and the refresh token
  // can't (or doesn't) rescue it. Clearing React state here makes
  // RequireAuth bounce the user to /login on the next render.
  // ---------------------------------------------------------------------
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<AuthLogoutDetail>).detail;
      // authFetch already cleared localStorage; we just need to flip state.
      // Skip for 'manual' (the local logout() above already did setToken(null))
      // to avoid an extra render.
      if (detail?.reason === 'manual') return;
      clearAuthToken();
      clearRefreshToken();
      clearTokenExpiry();
      clearStoredUser();
      setToken(null);
      setUser(null);
    };
    if (typeof window !== 'undefined') {
      window.addEventListener(AUTH_LOGOUT_EVENT, handler as EventListener);
      return () => window.removeEventListener(AUTH_LOGOUT_EVENT, handler as EventListener);
    }
    return undefined;
  }, []);

  // ---------------------------------------------------------------------
  // Boot-time check — if a previous session's token is past its expiry
  // AND there's no refresh token to rescue it, drop the stale local state
  // immediately so RequireAuth doesn't briefly render protected pages
  // before the first API call comes back 401.
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!token) return;
    if (isTokenExpired() && !getRefreshToken()) {
      clearAllAuth();
      setToken(null);
      setUser(null);
    }
    // Token still valid, or we have a refresh token — authFetch will
    // refresh on the next request, no eager action needed.
    // The dependency array is intentionally empty: this is a one-shot
    // boot check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bootstrap the multi-tenant identity on first mount whenever a
  // bearer is already stored. Refresh on logout clears me; logout from
  // the global event clears it via setUser(null) handler above.
  useEffect(() => {
    if (token) void refreshMe();
    else setMe(null);
  }, [token, refreshMe]);

  // Resolve the active account: honour the persisted selection when it
  // still matches an account the user belongs to, otherwise fall back to
  // the first one. This keeps the UI coherent if a membership is revoked.
  const currentAccount: AccountSummary | null = useMemo(() => {
    const accounts = me?.accounts ?? [];
    if (accounts.length === 0) return null;
    const chosen = activeAccountId
      ? accounts.find((a) => a.id === activeAccountId)
      : undefined;
    return chosen ?? accounts[0];
  }, [me, activeAccountId]);

  // Keep the persisted id in sync once the account list resolves — if the
  // saved account is gone, snap to the fallback so reloads are stable.
  useEffect(() => {
    if (!currentAccount) return;
    if (activeAccountId !== currentAccount.id) {
      setActiveAccountId(currentAccount.id);
      setActiveAccountIdState(currentAccount.id);
      // Mirror the resolved workspace to the server so backend data scoping
      // defaults to it even when a request omits ?account=.
      void setActiveWorkspace(currentAccount.id).catch(() => { /* non-fatal */ });
    }
  }, [currentAccount, activeAccountId]);

  const value = useMemo<AuthState>(
    () => ({
      user, token,
      isAuthenticated: Boolean(token),
      isLoggingIn, loginError, login,
      isSigningUp, signupError, signup,
      isConfirming, confirmError, confirm,
      resendCode,
      logout,
      me, currentAccount, setCurrentAccount, meLoading, refreshMe,
    }),
    [
      user, token,
      isLoggingIn, loginError, login,
      isSigningUp, signupError, signup,
      isConfirming, confirmError, confirm,
      resendCode,
      logout,
      me, currentAccount, setCurrentAccount, meLoading, refreshMe,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
